import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { WifiOff } from 'lucide-react';
import { t } from '@/lib/i18n';

const LEGACY_WORKBOX_CACHE_PREFIXES = ['workbox-precache', 'workbox-runtime'];

/** Retire only this dashboard's legacy root worker and Workbox caches. */
function LegacyServiceWorkerCleanup() {
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const dashboardScope = `${window.location.origin}/`;
        void navigator.serviceWorker.getRegistrations().then((registrations) => (
            Promise.all(
                registrations
                    .filter((registration) => registration.scope === dashboardScope)
                    .map((registration) => registration.unregister()),
            )
        ));

        if ('caches' in window) {
            void caches.keys().then((cacheNames) => (
                Promise.all(
                    cacheNames
                        .filter((cacheName) => LEGACY_WORKBOX_CACHE_PREFIXES.some(
                            (prefix) => cacheName.startsWith(prefix),
                        ))
                        .map((cacheName) => caches.delete(cacheName)),
                )
            ));
        }
    }, []);

    return null;
}

/**
 * Slim offline banner. Admin work (orders, inventory) can fail silently when the
 * connection drops, so we warn explicitly and confirm when it returns. Uses
 * logical insets + safe-area padding so it behaves under RTL and on notched
 * devices.
 */
function OfflineBanner() {
    const [online, setOnline] = useState(() =>
        typeof navigator === 'undefined' ? true : navigator.onLine,
    );
    const wasOffline = useRef(false);

    useEffect(() => {
        const goOnline = () => {
            setOnline(true);
            if (wasOffline.current) {
                toast.success(t('errors.offline.restored'), { id: 'pwa-offline' });
                wasOffline.current = false;
            }
        };
        const goOffline = () => {
            setOnline(false);
            wasOffline.current = true;
        };
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    if (online) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed inset-x-0 bottom-0 z-[60] flex items-center justify-center gap-2 bg-foreground px-4 py-2 text-center text-xs font-medium text-background"
            style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
            <WifiOff className="h-3.5 w-3.5 shrink-0" />
            <span>{t('errors.offline.message')}</span>
        </div>
    );
}

export function PwaPrompts() {
    return (
        <>
            <LegacyServiceWorkerCleanup />
            <OfflineBanner />
        </>
    );
}
